import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Truck, Plus, AlertTriangle, Receipt, FileText, ClipboardCheck, FilterX } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ProjectFilter } from '@/components/ProjectFilter';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { UploadStatusRow } from '@/components/common/UploadStatusRow';
import type { UploadChipStatus } from '@/components/common/UploadStatusChip';

interface Summary { activeTrips: number; inTransit: number; awaitingPaymentSar: number; thisMonthRevenueSar: number }
interface WasteSummary { month: string; total_active_waste_sar: number; flagged_count: number }
interface Row {
  id: string; trip_code: string; status: string;
  client_id: string | null; client_name: string | null;
  pickup_city: string; delivery_city: string;
  rate_sar: string; container_type: string | null; container_number: string | null;
  vehicle_id: string | null; vehicle_plate: string | null;
  driver_id: string | null; driver_name: string | null;
  scheduled_pickup_at: string | null; pickup_started_at: string | null; delivery_completed_at: string | null;
  expected_distance_km: number | null;
  expected_payment_date: string | null; invoiced_at: string | null; paid_at: string | null;
  created_at: string;
  // PR-CHIPS MAY 11 — server-computed upload status per chip slot.
  uploadStatus?: {
    loadedTruck:      UploadChipStatus;
    senderReceipt:    UploadChipStatus;
    wasika:           UploadChipStatus;
    deliveryTruck:    UploadChipStatus;
    deliveryDocument: UploadChipStatus;
  };
}

type MissingFilter = '' | 'loaded_truck' | 'sender_receipt' | 'wasika' | 'delivery_truck' | 'delivery_document' | 'any';

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_TONE: Record<string, string> = {
  quoted: 'bg-muted text-muted-foreground',
  assigned: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  en_route_to_pickup: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  at_pickup: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  loaded: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  in_transit: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
  at_delivery: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  unloaded: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  returning: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  completed: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  invoiced: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  paid: 'bg-emerald-700/15 text-emerald-800 dark:text-emerald-300',
  cancelled: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
};

type Tab = 'all' | 'active' | 'completed' | 'pending_payment' | 'cancelled';

export default function FlatbedDashboardPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [waste, setWaste] = useState<WasteSummary | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [tab, setTab] = useState<Tab>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // PR MAY10 P4B.1: project filter on trip list
  const [projectId, setProjectId] = useState<string | null>(null);
  // PR-CHIPS MAY 11 — missing-doc filter
  const [missing, setMissing] = useState<MissingFilter>('');
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (tab === 'active')          params.set('status', 'in_transit');
      else if (tab === 'completed')  params.set('status', 'completed');
      else if (tab === 'pending_payment') {
        // PR MAY10 P4A.3: pending payment = any unpaid state, not just invoiced.
        params.set('paymentStatus', 'pending,partially_paid,overdue,unset');
      }
      else if (tab === 'cancelled')  params.set('status', 'cancelled');
      if (projectId) params.set('projectId', projectId);
      if (missing)   params.set('missing', missing);
      const [s, r, w] = await Promise.all([
        apiClient.get<Summary>('/api/v1/flatbed/dashboard'),
        apiClient.get<Row[]>('/api/v1/flatbed/trips?' + params.toString()),
        apiClient.get<WasteSummary>('/api/v1/flatbed-waste/summary').catch(() => null),
      ]);
      setSummary(s.data);
      setRows(r.data);
      setWaste(w?.data ?? null);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tab, projectId, missing]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Truck className="h-4 w-4" /> Flatbed trips</div>
          <Link to="/dashboard/flatbed/new"><Button size="sm" className="gap-2"><Plus className="h-4 w-4" /> New trip</Button></Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Active" value={String(summary.activeTrips)} />
            <Stat label="In transit" value={String(summary.inTransit)} />
            <Stat label="Awaiting payment" value={fmtSar(summary.awaitingPaymentSar)} tone={summary.awaitingPaymentSar > 0 ? 'warn' : undefined} />
            <Stat label="Revenue MTD" value={fmtSar(summary.thisMonthRevenueSar)} tone="good" />
            <Stat label="Active waste MTD" value={waste ? fmtSar(waste.total_active_waste_sar) : '—'} tone={waste && waste.total_active_waste_sar > 0 ? 'warn' : undefined} />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <ProjectFilter value={projectId} onChange={setProjectId} storageKey="flatbedDashboard.projectId" />
          {/* PR-CHIPS MAY 11 — Missing-doc filter */}
          <select
            value={missing}
            onChange={(e) => setMissing(e.target.value as MissingFilter)}
            className="h-9 px-3 rounded border border-border bg-background text-sm"
            title="Filter trips by missing required documents"
          >
            <option value="">All docs</option>
            <option value="any">Missing any required doc</option>
            <option value="loaded_truck">Missing loaded-truck photo</option>
            <option value="sender_receipt">Missing sender receipt</option>
            <option value="wasika">Missing wasika PDF</option>
            <option value="delivery_truck">Missing delivery-truck photo</option>
            <option value="delivery_document">Missing delivery document</option>
          </select>
          {missing && (
            <Button variant="ghost" size="sm" className="gap-1" onClick={() => setMissing('')}>
              <FilterX className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>

        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {([
            { v: 'all', l: 'All' },
            { v: 'active', l: 'In transit' },
            { v: 'completed', l: 'Completed' },
            { v: 'pending_payment', l: 'Pending payment' },
            { v: 'cancelled', l: 'Cancelled' },
          ] as const).map((x) => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-3 py-2 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {x.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <PageError error={error} retry={refresh} />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No trips in this view.</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-muted/40 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Client</th>
                    <th className="text-left px-3 py-2">Route</th>
                    <th className="text-left px-3 py-2">Vehicle / Driver</th>
                    <th className="text-left px-3 py-2">DOCS</th>
                    <th className="text-right px-3 py-2">Rate</th>
                    <th className="text-end px-3 py-2 w-12" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link to={'/dashboard/flatbed/' + t.id} className="hover:underline">{t.trip_code}</Link>
                      </td>
                      <td className="px-3 py-2">
                        <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (STATUS_TONE[t.status] ?? '')}>
                          {t.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{t.client_name ?? '—'}</td>
                      <td className="px-3 py-2 text-xs">{t.pickup_city} → {t.delivery_city}</td>
                      <td className="px-3 py-2 text-xs">
                        {t.vehicle_plate ?? '—'}{t.driver_name ? ' · ' + t.driver_name : ''}
                      </td>
                      <td className="px-3 py-2">
                        {t.uploadStatus && (
                          <UploadStatusRow chips={[
                            { status: t.uploadStatus.loadedTruck,      icon: Truck,           label: 'Loaded-truck photo',
                              onClick: () => navigate('/dashboard/flatbed/' + t.id + '?focus=loaded-truck') },
                            { status: t.uploadStatus.senderReceipt,    icon: Receipt,         label: 'Sender receipt',
                              onClick: () => navigate('/dashboard/flatbed/' + t.id + '?focus=sender-receipt') },
                            { status: t.uploadStatus.wasika,           icon: FileText,        label: 'Wasika PDF',
                              onClick: () => navigate('/dashboard/flatbed/' + t.id + '?focus=wasika') },
                            { status: t.uploadStatus.deliveryTruck,    icon: Truck,           label: 'Delivery-truck photo',
                              onClick: () => navigate('/dashboard/flatbed/' + t.id + '?focus=delivery-truck') },
                            { status: t.uploadStatus.deliveryDocument, icon: ClipboardCheck,  label: 'Delivery document',
                              onClick: () => navigate('/dashboard/flatbed/' + t.id + '?focus=delivery-document') },
                          ]} />
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtSar(Number(t.rate_sar))}</td>
                      <td className="px-3 py-2 text-end">
                        <RowDeleteButton
                          endpoint={`/api/v1/flatbed/trips/${t.id}`}
                          entityKind="flatbed trip"
                          label={t.trip_code}
                          onDeleted={refresh}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' }) {
  const cls = tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : '';
  return (
    <div className="bg-muted rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={'text-xl font-medium mt-0.5 ' + cls}>{value}</div>
    </div>
  );
}

